// ═══ 국내 매각 차량 불러오기 모달 (세금계산서 발행 프리필) ═══
// GET /tax-invoice-drafts/source-cars 목록에서 차량을 다중선택 → 발행 폼 프리필.
// 서버가 바이어 사업자번호 10자리인 차량만 반환 (해외 바이어는 발행 대상 아님).
// 홈택스 양식은 행당 품목 4개 제한 → 5대 이상 선택 시 4대 단위 draft 분할 저장.
// 설계: erp/app/docs/tax-hometax-export/plan-v2-data-ux.md §1 / §3
import React, { useState, useEffect } from 'react';
import { API, authFetch } from '../utils/api';

var todayYmd = function () {
  return new Date().toISOString().slice(0, 10).replace(/-/g, '');
};

// 차량 배열 → 발행 폼 품목 배열 (1차량 = 1품목)
var buildCarItems = function (cars, taxType) {
  var date = todayYmd();
  return cars.map(function (c) {
    var supply = parseInt(c.sellPrice) || 0;
    return {
      date: date,
      itemName: c.carType || c.carNo || '',
      spec: c.vin || '',
      qty: '1',
      unitCost: String(supply),
      supplyCost: String(supply),
      tax: String(taxType === '\uACFC\uC138' ? Math.round(supply * 0.1) : 0),
      note: ''
    };
  });
};

// 5대 이상 → 4대 단위 draft 연속 저장 (POST /tax-invoice-drafts, sourceType=car_sale)
var saveCarSaleDrafts = function (cars, opts) {
  var taxType = opts.taxType;
  var purposeType = opts.purposeType;
  var buyer = (cars[0] && cars[0].buyer) || {};
  var bizNo = String(buyer.bizNo || '').replace(/-/g, '');
  if (bizNo.length !== 10) {
    return Promise.reject(new Error('\uBC14\uC774\uC5B4 \uC0AC\uC5C5\uC790\uBC88\uD638\uAC00 \uC5C6\uC5B4 \uC77C\uAD04 \uC800\uC7A5\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uBC14\uC774\uC5B4 \uC815\uBCF4\uC5D0 \uC0AC\uC5C5\uC790\uBC88\uD638\uB97C \uB4F1\uB85D\uD558\uAC70\uB098 4\uB300 \uC774\uD558\uB85C \uC120\uD0DD\uD574 \uC9C1\uC811 \uC785\uB825\uD558\uC138\uC694.'));
  }
  var chunks = [];
  var i = 0;
  for (i = 0; i < cars.length; i += 4) chunks.push(cars.slice(i, i + 4));

  return chunks.reduce(function (chain, chunk) {
    return chain.then(function () {
      var items = buildCarItems(chunk, taxType);
      var supplyTotal = items.reduce(function (s, it) { return s + (parseInt(it.supplyCost) || 0); }, 0);
      var taxTotal = items.reduce(function (s, it) { return s + (parseInt(it.tax) || 0); }, 0);
      var body = {
        writeDate: todayYmd(), taxType: taxType, purposeType: purposeType,
        invoiceeCorpNum: bizNo, invoiceeCorpName: buyer.name || '', invoiceeCeoName: buyer.ceo || '',
        invoiceeAddr: buyer.addr || '', invoiceeEmail: buyer.email || '',
        supplyCostTotal: supplyTotal, taxTotal: taxTotal,
        sourceType: 'car_sale',
        sourceCarIds: chunk.map(function (c) { return c.carId; }),
        items: items.map(function (it) {
          return { name: it.itemName, spec: it.spec, qty: 1, unitCost: parseInt(it.unitCost) || 0, supplyCost: parseInt(it.supplyCost) || 0, tax: parseInt(it.tax) || 0 };
        })
      };
      return authFetch(API + '/tax-invoice-drafts', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      }).then(function (r) { return r.json(); }).then(function (d) {
        if (!d.success) throw new Error(d.error || '\uC800\uC7A5 \uC2E4\uD328');
      });
    });
  }, Promise.resolve()).then(function () { return chunks.length; });
};

function TaxInvoiceCarPickModal({ showToast, onClose, onConfirm }) {
  var [q, setQ] = useState('');
  var [cars, setCars] = useState([]);
  var [loading, setLoading] = useState(false);
  var [picked, setPicked] = useState([]);

  var load = function (keyword) {
    setLoading(true);
    var url = API + '/tax-invoice-drafts/source-cars' + (keyword ? '?q=' + encodeURIComponent(keyword) : '');
    authFetch(url).then(function (r) { return r.json(); }).then(function (d) {
      setCars(d.cars || []);
      setLoading(false);
    }).catch(function () {
      setLoading(false);
      showToast('\uCC28\uB7C9 \uBAA9\uB85D \uC870\uD68C \uC2E4\uD328', 'err');
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(function () { load(''); }, []);

  // 첫 선택 바이어로 잠금 — 다른 바이어 차량은 같은 계산서에 담을 수 없음
  var lockedBuyerId = picked.length ? picked[0].buyerId : null;
  var isPicked = function (carId) {
    return picked.some(function (c) { return c.carId === carId; });
  };

  var toggle = function (car) {
    if (lockedBuyerId != null && car.buyerId !== lockedBuyerId && !isPicked(car.carId)) return;
    setPicked(function (prev) {
      return prev.some(function (c) { return c.carId === car.carId; })
        ? prev.filter(function (c) { return c.carId !== car.carId; })
        : prev.concat(car);
    });
  };

  var handleConfirm = function () {
    if (picked.length === 0) { showToast('\uCC28\uB7C9\uC744 1\uB300 \uC774\uC0C1 \uC120\uD0DD\uD574\uC8FC\uC138\uC694', 'err'); return; }
    onConfirm(picked);
  };

  var totalSupply = picked.reduce(function (s, c) { return s + (parseInt(c.sellPrice) || 0); }, 0);
  var th = { padding: '8px 6px', borderBottom: '2px solid #e2e8f0', textAlign: 'left', fontWeight: 700, color: '#475569' };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div className="card" style={{ width: 880, maxWidth: '94vw', maxHeight: '86vh', background: '#fff', borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column' }} onClick={function (e) { e.stopPropagation(); }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ fontSize: 15, margin: 0, color: '#1B3A5C' }}>{'\uAD6D\uB0B4 \uB9E4\uAC01 \uCC28\uB7C9 \uBD88\uB7EC\uC624\uAE30'}</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: '#94a3b8' }}>{'\u2715'}</button>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          <input
            style={{ flex: 1, padding: '7px 10px', border: '1px solid #d0d5dd', borderRadius: 6, fontSize: 12, outline: 'none' }}
            value={q}
            onChange={function (e) { setQ(e.target.value); }}
            onKeyDown={function (e) { if (e.key === 'Enter') load(q); }}
            placeholder={'\uCC28\uB7C9\uBC88\uD638 / \uCC28\uC885 / \uCC28\uB300\uBC88\uD638 \uAC80\uC0C9'}
          />
          <button onClick={function () { load(q); }} style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid #d0d5dd', background: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{'\uAC80\uC0C9'}</button>
        </div>

        <div style={{ fontSize: 11, color: '#64748b', background: '#f8fafc', borderRadius: 6, padding: '8px 12px', marginBottom: 10, lineHeight: 1.6 }}>
          {'\uAD6D\uB0B4 \uC0AC\uC5C5\uC790(\uC0AC\uC5C5\uC790\uBC88\uD638\uAC00 \uB4F1\uB85D\uB41C \uBC14\uC774\uC5B4)\uC5D0\uAC8C \uB9E4\uAC01\uD55C \uCC28\uB7C9\uB9CC \uD45C\uC2DC\uB429\uB2C8\uB2E4. \uD574\uC678 \uBC14\uC774\uC5B4 \uB4F1 \uC0AC\uC5C5\uC790\uBC88\uD638\uAC00 \uC5C6\uB294 \uAC74\uC740 \uC138\uAE08\uACC4\uC0B0\uC11C \uBC1C\uD589 \uB300\uC0C1\uC774 \uC544\uB2C8\uBA70, \uBC14\uC774\uC5B4 \uC815\uBCF4\uC5D0 \uC0AC\uC5C5\uC790\uBC88\uD638\uB97C \uB4F1\uB85D\uD558\uBA74 \uBAA9\uB85D\uC5D0 \uB098\uD0C0\uB0A9\uB2C8\uB2E4.'}
          <br />
          {'\uD55C \uACC4\uC0B0\uC11C\uC5D0\uB294 \uAC19\uC740 \uBC14\uC774\uC5B4\uC758 \uCC28\uB7C9\uB9CC \uB2F4\uAE38 \uC218 \uC788\uC2B5\uB2C8\uB2E4. 4\uB300\uAE4C\uC9C0\uB294 \uBC1C\uD589 \uD3FC\uC5D0 \uD504\uB9AC\uD544\uB418\uACE0, 5\uB300 \uC774\uC0C1\uC740 4\uB300\uC529 \uB098\uB204\uC5B4 \uBC1C\uD589 \uBAA9\uB85D\uC5D0 \uC790\uB3D9 \uC800\uC7A5\uB429\uB2C8\uB2E4.'}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', minHeight: 200 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>{'\uB85C\uB529 \uC911...'}</div>
          ) : cars.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>{'\uBD88\uB7EC\uC62C \uAD6D\uB0B4 \uB9E4\uAC01 \uCC28\uB7C9\uC774 \uC5C6\uC2B5\uB2C8\uB2E4 (\uBC14\uC774\uC5B4 \uC0AC\uC5C5\uC790\uBC88\uD638\u00B7\uD310\uB9E4\uAE08\uC561\uC774 \uB4F1\uB85D\uB41C \uCC28\uB7C9\uB9CC \uD45C\uC2DC)'}</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={Object.assign({}, th, { width: 32 })}></th>
                  {['\uCC28\uB7C9\uBC88\uD638', '\uCC28\uC885', '\uCC28\uB300\uBC88\uD638', '\uBC14\uC774\uC5B4', '\uD310\uB9E4\uAE08\uC561', '\uC0C1\uD0DC'].map(function (h, i) {
                    return <th key={i} style={th}>{h}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {cars.map(function (c) {
                  var checked = isPicked(c.carId);
                  var blocked = lockedBuyerId != null && c.buyerId !== lockedBuyerId && !checked;
                  return (
                    <tr key={c.carId} onClick={function () { toggle(c); }} style={{ borderBottom: '1px solid #f1f5f9', background: checked ? '#eff6ff' : blocked ? '#f8fafc' : '#fff', color: blocked ? '#cbd5e1' : '#1e293b', cursor: blocked ? 'not-allowed' : 'pointer' }}>
                      <td style={{ padding: '8px 6px', textAlign: 'center' }}>
                        <input type="checkbox" checked={checked} disabled={blocked} readOnly />
                      </td>
                      <td style={{ padding: '8px 6px', fontWeight: 600 }}>{c.carNo || '-'}</td>
                      <td style={{ padding: '8px 6px' }}>{c.carType || '-'}</td>
                      <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: 11 }}>{c.vin || '-'}</td>
                      <td style={{ padding: '8px 6px' }}>
                        {(c.buyer && c.buyer.name) || '-'}
                      </td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 600 }}>{(parseInt(c.sellPrice) || 0).toLocaleString()}</td>
                      <td style={{ padding: '8px 6px' }}>
                        {c.draftId ? (
                          <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: '#fef3c7', color: '#d97706' }}>{'\uBC1C\uD589\uB428'}</span>
                        ) : (
                          <span style={{ fontSize: 10, color: '#94a3b8' }}>{c.saleStatus || '-'}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 12, color: '#475569' }}>
            {'\uC120\uD0DD '}<b>{picked.length}</b>{'\uB300 \u00B7 \uACF5\uAE09\uAC00\uC561 '}<b>{totalSupply.toLocaleString()}</b>
            {picked.length > 4 && <span style={{ marginLeft: 8, color: '#0891b2', fontWeight: 700 }}>{'\u2192 ' + Math.ceil(picked.length / 4) + '\uAC74\uC73C\uB85C \uBD84\uD560 \uC800\uC7A5'}</span>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d0d5dd', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{'\uCDE8\uC18C'}</button>
            <button onClick={handleConfirm} disabled={picked.length === 0} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: picked.length === 0 ? '#94a3b8' : '#2563eb', color: '#fff', fontSize: 13, fontWeight: 700, cursor: picked.length === 0 ? 'default' : 'pointer' }}>{'\uBD88\uB7EC\uC624\uAE30'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { buildCarItems, saveCarSaleDrafts };
export default TaxInvoiceCarPickModal;
